import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { AttachFile, Delete, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import PaymentBreakdownTable from './PaymentBreakdownTable';
import { DEFAULT_PLATFORM_FEES } from '../utils/feeCalculation';
import { buildCustomerPaymentBreakdownLines } from '../utils/paymentBreakdownLines';

const ATTACHMENT_MAX_MB = 25;

export interface ProviderBidDetailsModel {
  id?: string;
  _id?: string;
  price: number;
  status: string;
  createdAt: string;
  updatedAt?: string;
  platformFeePercentage?: number;
  servicePricing?: any[];
  requestId?: any;
  isAdditionalServices?: boolean;
  serviceRequestStatus?: string | null;
  providerEstimateAttachment?: {
    name: string;
    mimeType?: string;
    uploadedAt?: string;
    viewUrl?: string | null;
    missing?: boolean;
  } | null;
  requestData?: {
    address?: any;
    propertyType?: string;
    description?: string;
    customerId?: any;
  } | null;
}

function bidKey(bid: ProviderBidDetailsModel | null | undefined): string {
  return String(bid?.id || bid?._id || '');
}

function requestIdString(bid: ProviderBidDetailsModel | null | undefined): string | null {
  if (!bid?.requestId) return null;
  if (typeof bid.requestId === 'string') return bid.requestId;
  return bid.requestId._id ? String(bid.requestId._id) : null;
}

function getFrequencyLabel(frequency: string) {
  const frequencyMap: Record<string, string> = {
    weekly: 'Weekly',
    'bi-weekly': 'Bi-weekly',
    monthly: 'Monthly',
    'per-instance': 'Per Instance',
    'seasonal-contract': 'Seasonal Contract',
  };
  return frequencyMap[frequency] || frequency;
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatPropertyType(propertyType: string) {
  if (propertyType === 'single_residential') return 'Single Family';
  if (propertyType === 'multi_family_condo') return 'Multi-Family/Condo';
  if (propertyType === 'commercial') return 'Commercial';
  return propertyType;
}

async function fetchCanonicalBidDetails(bidId: string): Promise<ProviderBidDetailsModel> {
  const res = await apiService.get(`/bids/${bidId}`);
  if (!res.success || !res.data) {
    throw new Error(res.error || 'Failed to load bid details');
  }
  const data = res.data as any;
  const populatedRequest =
    data.requestId && typeof data.requestId === 'object' ? data.requestId : null;
  return {
    ...data,
    id: String(data.id || data._id),
    requestId: populatedRequest?._id || data.requestId || null,
    requestData:
      data.requestData ||
      (populatedRequest
        ? {
            address: populatedRequest.address,
            propertyType: populatedRequest.propertyType,
            description: populatedRequest.description,
            customerId: populatedRequest.customerId,
          }
        : null),
  };
}

interface ProviderBidDetailsDialogProps {
  open: boolean;
  bidId: string | null;
  onClose: () => void;
  /** Called after attachment changes so parent lists can refresh */
  onUpdated?: (bid: ProviderBidDetailsModel) => void;
}

/**
 * Canonical provider Bid Details dialog (Reviews & Bids).
 * Used from Dashboard My Bids and Reviews & Bids so both show the same details.
 */
const ProviderBidDetailsDialog: React.FC<ProviderBidDetailsDialogProps> = ({
  open,
  bidId,
  onClose,
  onUpdated,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bid, setBid] = useState<ProviderBidDetailsModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentMessage, setAttachmentMessage] = useState<string | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const loadBid = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const next = await fetchCanonicalBidDetails(id);
      setBid(next);
      return next;
    } catch (err: any) {
      setError(err.message || 'Failed to load bid details');
      setBid(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !bidId) {
      setBid(null);
      setError(null);
      setAttachmentMessage(null);
      return;
    }
    loadBid(bidId);
  }, [open, bidId, loadBid]);

  const canManageEstimateAttachment = (b: ProviderBidDetailsModel) => {
    if (!b?.isAdditionalServices || !requestIdString(b)) return false;
    const srStatus = b.serviceRequestStatus || '';
    return ['pending', 'bidding', 'accepted', 'scheduled', 'in-progress'].includes(srStatus);
  };

  const handleEstimateAttachmentSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const srId = requestIdString(bid);
    if (!file || !bid || !srId) return;
    if (file.size > ATTACHMENT_MAX_MB * 1024 * 1024) {
      setAttachmentMessage(`File must be ${ATTACHMENT_MAX_MB}MB or smaller`);
      return;
    }
    const wasAccepted =
      bid.status === 'accepted' ||
      ['accepted', 'scheduled', 'in-progress'].includes(bid.serviceRequestStatus || '');
    if (
      wasAccepted &&
      !window.confirm(
        'Replacing this document will require the customer to accept the estimate again before you can schedule. Continue?',
      )
    ) {
      return;
    }
    try {
      setAttachmentBusy(true);
      setAttachmentMessage(null);
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiService.post(`/additional-services/${srId}/estimate-attachment`, fd, {
        timeout: 120000,
      });
      if (!res.success) throw new Error(res.error || 'Failed to upload document');
      setAttachmentMessage(
        (res as any).message ||
          ((res.data as any)?.requiresReaccept
            ? 'Document updated. Customer must accept again.'
            : 'Document attached'),
      );
      const refreshed = await loadBid(bidKey(bid));
      if (refreshed) onUpdated?.(refreshed);
    } catch (err: any) {
      setAttachmentMessage(err.message || 'Failed to upload document');
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleRemoveEstimateAttachment = async () => {
    const srId = requestIdString(bid);
    if (!bid || !srId || !bid.providerEstimateAttachment) return;
    const wasAccepted =
      bid.status === 'accepted' ||
      ['accepted', 'scheduled', 'in-progress'].includes(bid.serviceRequestStatus || '');
    if (
      !window.confirm(
        wasAccepted
          ? 'Remove this document? The customer will need to accept the estimate again.'
          : 'Remove the attached estimate document?',
      )
    ) {
      return;
    }
    try {
      setAttachmentBusy(true);
      setAttachmentMessage(null);
      const res = await apiService.delete(`/additional-services/${srId}/estimate-attachment`);
      if (!res.success) throw new Error(res.error || 'Failed to remove document');
      setAttachmentMessage((res as any).message || 'Document removed');
      const refreshed = await loadBid(bidKey(bid));
      if (refreshed) onUpdated?.(refreshed);
    } catch (err: any) {
      setAttachmentMessage(err.message || 'Failed to remove document');
    } finally {
      setAttachmentBusy(false);
    }
  };

  const handleClose = () => {
    setAttachmentMessage(null);
    onClose();
  };

  const address = bid?.requestData?.address;
  const propertyType = bid?.requestData?.propertyType;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Bid Details</Typography>
          {bid && (
            <Chip
              label={bid.status.charAt(0).toUpperCase() + bid.status.slice(1)}
              size="small"
              color={
                bid.status === 'accepted'
                  ? 'success'
                  : bid.status === 'pending'
                    ? 'warning'
                    : bid.status === 'rejected'
                      ? 'error'
                      : 'default'
              }
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {!loading && bid && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Bid Amount
              </Typography>
              <Typography variant="h6">{formatCurrency(bid.price)}</Typography>
            </Grid>

            {bid.servicePricing && bid.servicePricing.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Services
                </Typography>
                {bid.servicePricing.map((sp: any, idx: number) => (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      {sp.serviceName} - {formatCurrency(sp.price)} ({getFrequencyLabel(sp.frequency)})
                    </Typography>
                  </Box>
                ))}
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Customer charge breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Same totals the customer sees (service price, sales tax, payment processing, total).
              </Typography>
              <PaymentBreakdownTable
                compact
                lines={buildCustomerPaymentBreakdownLines({
                  servicePricing: bid.servicePricing,
                  fallbackPrice: bid.price,
                  platformFeePercent:
                    bid.platformFeePercentage ?? DEFAULT_PLATFORM_FEES.standardFeePercentage,
                  salesTaxRates: (user as any)?.salesTaxRates,
                  address,
                  propertyType,
                })}
                footerNote="This matches the customer checkout breakdown. Sales tax (if any) is remitted by you. Payment processing is charged to the customer and is not deducted from your service price."
              />
            </Grid>

            {address && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Address
                </Typography>
                <Typography variant="body2">
                  {address.street}, {address.city}, {address.state} {address.zipCode}
                </Typography>
              </Grid>
            )}

            {propertyType && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Property Type
                </Typography>
                <Typography variant="body2">{formatPropertyType(propertyType)}</Typography>
              </Grid>
            )}

            {bid.requestData?.description && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2">{bid.requestData.description}</Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Submitted
              </Typography>
              <Typography variant="body2">{new Date(bid.createdAt).toLocaleString()}</Typography>
            </Grid>

            {bid.updatedAt && bid.updatedAt !== bid.createdAt && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Last Updated
                </Typography>
                <Typography variant="body2">{new Date(bid.updatedAt).toLocaleString()}</Typography>
              </Grid>
            )}

            {bid.isAdditionalServices && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Estimate Document
                </Typography>
                {attachmentMessage && (
                  <Alert
                    severity="info"
                    sx={{ mb: 1 }}
                    onClose={() => setAttachmentMessage(null)}
                  >
                    {attachmentMessage}
                  </Alert>
                )}
                {bid.providerEstimateAttachment?.name ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      icon={<AttachFile />}
                      label={bid.providerEstimateAttachment.name}
                      size="small"
                    />
                    {bid.providerEstimateAttachment.viewUrl && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNew />}
                        href={bid.providerEstimateAttachment.viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </Button>
                    )}
                    {bid.providerEstimateAttachment.missing && (
                      <Typography variant="caption" color="error">
                        File missing. Replace it to send the customer a working copy.
                      </Typography>
                    )}
                    {canManageEstimateAttachment(bid) && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={handleRemoveEstimateAttachment}
                        disabled={attachmentBusy}
                        title="Remove document"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No document attached to this estimate.
                  </Typography>
                )}
                {canManageEstimateAttachment(bid) && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      hidden
                      onChange={handleEstimateAttachmentSelect}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AttachFile />}
                      disabled={attachmentBusy}
                      onClick={() => attachmentInputRef.current?.click()}
                    >
                      {attachmentBusy
                        ? 'Uploading…'
                        : bid.providerEstimateAttachment
                          ? 'Replace Document'
                          : 'Attach Document'}
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        handleClose();
                        navigate('/provider/additional-services');
                      }}
                    >
                      Open Additional Services
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                      Changing the file after the customer accepted requires them to accept again.
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderBidDetailsDialog;
